import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { EmploymentWrapper, Separator, CurrentMonthlyWrapper } from './EmploymentInformation.styles';
import { Wrapper } from '../wrapper.styles';
import { TextInput } from '../../components/input/TextInput/TextInput';
import { TextBlock } from '@components/text/TextBlock';
import { TypeOfText } from '@components/text/TextBlock';
import { DropDownInput } from '@/components/input/DropDownInput/DropDownInput';
import { FormGroupComponent } from '@/components/input/FormGroup/FormGroup';
import { NavigationButton } from '@/components/Button/NavigationButton/NavigationButton';
import { useIsMobile } from '@/hooks/useIsMobile';
import { PageOrigin } from '@/components/Button/NavigationButton/NavigationButton';
import DateEmploymentPicker from '@/components/input/DateEmployedPicker/DateEmployedPicker';
import { Button } from '@/components/Button/DefaultButton/Button';
import { CheckConsent } from '@/components/input/CheckConsent/CheckConsent';
import _ from 'lodash';
import { useAppSelector, useAppDispatch } from '@app/hooks';
import { convertToNumber, formatNumber } from '@/utils/formatValues';
import { RadioButton } from '@/components/input/RadioButton/RadioButton';
import { selectIsGoingBack, selectIsFirstTimeEmploymentInformation, selectOpenWarningChangesEmploymentModal, setOpenWarningChangesEmploymentModal } from '@/reducers/currentPage';
import { monthsBetweenDateAndToday } from '@/utils/handleDate';
import { useDataChangeDetection } from '@/hooks/useDataChangeDetection';
import { DefaultModal } from '@/components/Modals/DefaultModal/DefaultModal';

interface EmploymentInformationProps {
  onAccess: (page: string) => void;
}

export enum PropertyType {
  Type = 'type',
  EmployerName = 'employment',
  JobTitle = 'jobTitle',
  StartDate = 'startDate',
  EndDate = 'endDate',
  GrossMonthly = 'monthly',
  CurrentEmployment = 'currentEmployment',
  ContactMethod = 'contactMethod',
  Citizenship = 'citizenship',
  TypeIncome = 'typeIncome',
  GrossMonthlyIncome = 'monthlyIncome',
  TypeCoBorrower = 'typeCoBorrower',
  EmploymentCoBorrower = 'employmentCoBorrower',
  JobTitleCoBorrower = 'jobTitleCoBorrower',
  StartDateCoBorrower = 'startDateCoBorrower',
  EndDateCoBorrower = 'endDateCoBorrower',
  MonthlyCoBorrower = 'monthlyCoBorrower',
  CurrentEmploymentCoBorrower = 'currentEmploymentCoBorrower',
  ContactMethodCoBorrower = 'contactMethodCoBorrower',
  CitizenshipCoBorrower = 'citizenshipCoBorrower',
  TypeIncomeCoBorrower = 'typeIncomeCoBorrower',
  GrossMonthlyIncomeCoBorrower = 'grossMonthlyIncomeCoBorrower',
  TotalMonthsCoBorrower = 'totalMonthsCoBorrower',
}

type EmploymentForm = {
  type: string;
  employment: string;
  jobTitle: string;
  startDate: string;
  endDate: string;
  monthly: string;
  currentEmployment: boolean;
  totalMonths: number;
  lessThanTwoYears: boolean;
};

type CoBorrowerForm = {
  jobTitleCoBorrower: string;
  startDateCoBorrower: string;
  endDateCoBorrower: string;
  monthlyCoBorrower: string;
  currentEmploymentCoBorrower: boolean;
  totalMonths: number;
  monthlyIncome: string;
  typeCoBorrower: string;
  employmentCoBorrower: string;
  lessThanTwoYearsCoBorrower: boolean;
};

type IncomeFormValues = {
  typeIncome: string;
  monthlyIncome: string;
};

type IncomeCoBorrowersFormValues = {
  typeIncomeCoBorrower: string;
  grossMonthlyIncomeCoBorrower: string;
};

const EmploymentInformation: React.FC<EmploymentInformationProps> = ({ onAccess }) => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const dispatch = useAppDispatch();
  const [formError, setFormError] = React.useState(false);
  const [isInitialLoad, setIsInitialLoad] = React.useState(true);
  const [formSubmitted, setFormSubmitted] = React.useState(false);
  let loanApplication = useAppSelector((state) => state.LoanApplication);
  const borrowerEmploy = loanApplication?.borrowers[0]?.employmentHistory?.length;
  const coborrowerEmploy = loanApplication?.borrowers[1]?.employmentHistory?.length;
  const borrowerIncome = loanApplication?.borrowers[0]?.otherIncome?.length;
  const coborrowerIncome = loanApplication?.borrowers[1]?.otherIncome?.length;
  const [selectedOption, setSelectedOption] = React.useState('');
  const [selectedCoBorrowerOption, setSelectedCoBorrowerOption] = React.useState('');
  const [indexForm, setIndexForm] = useState<number>(borrowerEmploy);
  const [indexIncomeForm, setIndexIncomeForm] = useState<number>(borrowerIncome);
  const [indexCoBorrowerForm, setIndexCoBorrowerForm] = useState<number>(coborrowerEmploy);
  const [indexIncomeCoBorrowerForm, setIndexIncomeCoBorrowerForm] = useState<number>(coborrowerIncome);
  const [otherMonthlyIncome, setOtherMonthlyIncome] = useState<number>(0);
  const [otherCoBorrowerMonthlyIncome, setOtherCoBorrowerMonthlyIncome] = useState<number>(0);
  const isGoingBack = useAppSelector(selectIsGoingBack);
  const isFirstTimeEmploymentInformation = useAppSelector(selectIsFirstTimeEmploymentInformation);
  const openWarningChangesEmploymentModal = useAppSelector(selectOpenWarningChangesEmploymentModal);
  const [isRemoveBorrowerForm, setIsRemoveBorrowerForm] = useState<boolean>(false);
  const [isRemoveIncomeForm, setIsRemoveIncomeForm] = useState<boolean>(false);
  const [isRemoveCoBorrowerForm, setIsRemoveCoBorrowerForm] = useState<boolean>(false);
  const [isRemoveIncomeCoBorrowerForm, setIsRemoveIncomeCoBorrowerForm] = useState<boolean>(false);
  const coBorrower = loanApplication?.borrowers.length > 1;
  let auxLoanApplication = _.cloneDeep(loanApplication);

  // employment data change detection
  const employmentKeysToMonitor = ['borrowerEmploymentHistory', 'borrowerOtherIncome', ...(coBorrower ? ['coBorrowerEmploymentHistory', 'coBorrowerOtherIncome'] : [])];
  const { hasDataChanged, resetChangeDetection } = useDataChangeDetection(employmentKeysToMonitor);
  const currentForm = useAppSelector((state) => state.CurrentForm);

  // Sync employment data to CurrentForm store for change detection
  useEffect(() => {
    // Update CurrentForm if employment data changed
    if (currentForm.borrowerEmploymentHistory !== JSON.stringify(loanApplication?.borrowers[0]?.employmentHistory || [])) {
      dispatch({
        type: 'CurrentForm/updateField',
        payload: {
          borrowerEmploymentHistory: JSON.stringify(loanApplication?.borrowers[0]?.employmentHistory || []),
          borrowerOtherIncome: JSON.stringify(loanApplication?.borrowers[0]?.otherIncome || []),
          ...(coBorrower && {
            coBorrowerEmploymentHistory: JSON.stringify(loanApplication?.borrowers[1]?.employmentHistory || []),
            coBorrowerOtherIncome: JSON.stringify(loanApplication?.borrowers[1]?.otherIncome || []),
          }),
        },
      });
    }
  }, [loanApplication?.borrowers, coBorrower, dispatch, currentForm.borrowerEmploymentHistory]); // eslint-disable-line react-hooks/exhaustive-deps

  const [dateValidationErrors, setDateValidationErrors] = useState<{ [key: string]: boolean }>({});
  const handleDateValidationChange = useCallback((isValid: boolean, index?: number, propertyType?: string) => {
    const key = `${propertyType}_${index}`;
    setDateValidationErrors((prev) => {
      const updated = { ...prev };
      if (isValid) {
        delete updated[key];
      } else {
        updated[key] = true;
      }
      return updated;
    });
  }, []);

  const [employmentForms, setEmploymentForms] = useState<EmploymentForm[]>(
    loanApplication.borrowers[0]?.employmentHistory?.map((employmentInfo) => ({
      type: employmentInfo.type || '',
      employment: employmentInfo.employerName || '',
      jobTitle: employmentInfo.jobTitle || '',
      startDate: employmentInfo.startDate || '',
      endDate: employmentInfo.endDate || '',
      monthly: formatNumber(employmentInfo.grossMonthlyIncome) || '',
      currentEmployment: employmentInfo.currentEmployment ? true : false,
      totalMonths: employmentInfo.totalMonths || 0,
      lessThanTwoYears: false,
    })) || [
      {
        type: '',
        employment: '',
        jobTitle: '',
        startDate: '',
        endDate: '',
        monthly: '',
        currentEmployment: false,
        totalMonths: 0,
      },
    ],
  );
  const [coBorrowerForms, setCoBorrowerForms] = useState<CoBorrowerForm[]>(
    loanApplication.borrowers[1]?.employmentHistory?.map((coBorrowerInfo) => ({
      jobTitleCoBorrower: coBorrowerInfo?.jobTitle || '',
      startDateCoBorrower: coBorrowerInfo?.startDate || '',
      endDateCoBorrower: !coBorrowerInfo?.currentEmployment && coBorrowerInfo?.endDate ? coBorrowerInfo?.endDate : '',
      monthlyCoBorrower: formatNumber(coBorrowerInfo?.grossMonthlyIncome) || '',
      currentEmploymentCoBorrower: coBorrowerInfo?.currentEmployment ? true : false,
      totalMonths: coBorrowerInfo?.totalMonths || 0,
      monthlyIncome: formatNumber(coBorrowerInfo?.grossMonthlyIncome) || '',
      typeCoBorrower: coBorrowerInfo?.type || '',
      employmentCoBorrower: coBorrowerInfo?.employerName || '',
      lessThanTwoYearsCoBorrower: false,
    })) || [
      {
        jobTitleCoBorrower: '',
        startDateCoBorrower: '',
        endDateCoBorrower: '',
        monthlyCoBorrower: '',
        currentEmploymentCoBorrower: false,
        monthlyIncome: '',
        typeCoBorrower: false,
        employmentCoBorrower: 0,
      },
    ],
  );
  const [incomeBorrowersForms, setIncomeBorrowerForms] = useState<IncomeFormValues[]>(
    loanApplication.borrowers[0]?.otherIncome?.map((incomeInfo) => ({
      typeIncome: incomeInfo.incomeType || '',
      monthlyIncome: formatNumber(incomeInfo.grossMonthlyIncome) || '',
    })) || [],
  );

  const [incomeCoBorrowersForms, setIncomeCoBorrowerForms] = useState<IncomeCoBorrowersFormValues[]>(
    loanApplication.borrowers[1]?.otherIncome?.map((incomeInfo) => ({
      typeIncomeCoBorrower: incomeInfo.incomeType || '',
      grossMonthlyIncomeCoBorrower: formatNumber(incomeInfo.grossMonthlyIncome) || '',
    })) || [],
  );
  const [currentIndex, setCurrentIndex] = useState<number[]>(
    employmentForms.map((employmentInfo, index) => (employmentInfo.currentEmployment ? index : null)).filter((index) => index !== null) as number[],
  );

  const [currentCoBorrowerIndex, setCurrentCoBorrowerIndex] = useState<number[]>(
    coBorrowerForms?.map((employmentInfo, index) => (employmentInfo.currentEmploymentCoBorrower ? index : null)).filter((index) => index !== null) as number[],
  );
  const isCurrentEmployment = selectedOption !== '' || employmentForms[indexForm]?.type === 'Retired' || employmentForms[indexForm]?.type === 'Unemployed' ? true : false;
  const isCurrentCoBorrowerEmployment =
    selectedCoBorrowerOption !== '' || coBorrowerForms[indexCoBorrowerForm]?.typeCoBorrower === 'Retired' || coBorrowerForms[indexCoBorrowerForm]?.typeCoBorrower === 'Unemployed'
      ? true
      : false;
  const [changeIndex, setChangeIndex] = useState(0);
  const currentEmploymentDates = employmentForms[changeIndex] || {};
  const currentCoBorrowerDates = coBorrowerForms[changeIndex] || {};
  const { startDateCoBorrower, endDateCoBorrower, currentEmploymentCoBorrower, monthlyCoBorrower } = currentCoBorrowerDates;
  const { startDate, endDate, currentEmployment, monthly } = currentEmploymentDates;
  const [totalBorrowerMonths, setTotalBorrowerMonths] = useState(0);
  const [totalCoBorrowerMonths, setTotalCoBorrowerMonths] = useState(0);
  const [backPage, setBackPage] = useState(false);
  const hasLessThanTwoYears = totalBorrowerMonths >= 24;
  const hasCoBorrowerLessThanTwoYears = totalCoBorrowerMonths >= 24;
  const [hasLessThanTwoYearsConsent, setHasLessThanTwoYearsConsent] = useState(false);
  const [hasCoBorrowerLessThanTwoYearsConsent, setHasCoBorrowerLessThanTwoYearsConsent] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    if (isGoingBack) {
      setBackPage(true);
      // show warning modal if data has changed and it's not the first time visiting this page
      if (!isFirstTimeEmploymentInformation && hasDataChanged()) {
        dispatch(setOpenWarningChangesEmploymentModal(true));
      }
    }
  }, [isGoingBack, isFirstTimeEmploymentInformation, hasDataChanged, dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isInitialized && loanApplication?.borrowers?.[1]?.employmentHistory) {
      const initialForms = loanApplication.borrowers[1].employmentHistory.map((history) => ({
        jobTitleCoBorrower: history.jobTitle || '',
        startDateCoBorrower: history.startDate || '',
        endDateCoBorrower: history.endDate || '',
        monthlyCoBorrower: history.grossMonthlyIncome ? `$${history.grossMonthlyIncome}` : '$0',
        currentEmploymentCoBorrower: history.currentEmployment || false,
        totalMonths: history.totalMonths || 0,
        monthlyIncome: history.grossMonthlyIncome ? `$${history.grossMonthlyIncome}` : '$0',
        typeCoBorrower: history.type || '',
        employmentCoBorrower: history.employerName || '',
        lessThanTwoYearsCoBorrower: false,
      }));

      setCoBorrowerForms(initialForms);
      setIsInitialized(true);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isInitialized && coBorrowerForms.length > 0) {
      const updatedEmploymentHistory = coBorrowerForms.map((form) => ({
        appendValue: loanApplication?.borrowers[1]?.appendValue || 0,
        type: form.typeCoBorrower,
        employerName: form.employmentCoBorrower,
        jobTitle: form.jobTitleCoBorrower,
        startDate: form.startDateCoBorrower,
        endDate: form.endDateCoBorrower,
        grossMonthlyIncome: parseFloat(form.monthlyCoBorrower.replace('$', '')) || 0,
        currentEmployment: form.currentEmploymentCoBorrower,
        totalMonths: form.totalMonths,
      }));

      const updatedLoanApplication = {
        ...loanApplication,
        borrowers: [
          loanApplication.borrowers[0],
          {
            ...loanApplication.borrowers[1],
            employmentHistory: updatedEmploymentHistory,
          },
        ],
      };

      dispatch({
        type: 'LoanApplication/updateValue',
        payload: updatedLoanApplication,
      });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAddForm = (type: 'employment' | 'coBorrower' | 'incomeBorrower' | 'incomeCoBorrower') => {
    if (type === 'employment') {
      setIndexForm(indexForm + 1);
      return setEmploymentForms((prevForms) => [
        ...prevForms,
        {
          type: '',
          employment: '',
          jobTitle: '',
          startDate: '',
          endDate: '',
          monthly: '',
          currentEmployment: false,
          totalMonths: 0,
          lessThanTwoYears: false,
        },
      ]);
    } else if (type === 'coBorrower') {
      setIndexCoBorrowerForm(indexCoBorrowerForm + 1);
      setCoBorrowerForms((prevForms) => [
        ...prevForms,
        {
          jobTitleCoBorrower: '',
          startDateCoBorrower: '',
          endDateCoBorrower: '',
          monthlyCoBorrower: '',
          currentEmploymentCoBorrower: false,
          typeIncomeCoBorrower: '',
          grossMonthlyIncomeCoBorrower: '',
          totalMonths: 0,
          monthlyIncome: '',
          typeCoBorrower: '',
          employmentCoBorrower: '',
          lessThanTwoYearsCoBorrower: false,
        },
      ]);
    } else if (type === 'incomeBorrower') {
      setIndexIncomeForm(indexIncomeForm + 1);
      setIncomeBorrowerForms((prevForms) => [
        ...prevForms,
        {
          typeIncome: '',
          monthlyIncome: '',
        },
      ]);
    } else {
      setIndexIncomeCoBorrowerForm(indexIncomeCoBorrowerForm + 1);
      setIncomeCoBorrowerForms((prevForms) => [
        ...prevForms,
        {
          typeIncomeCoBorrower: '',
          grossMonthlyIncomeCoBorrower: '',
        },
      ]);
    }
  };

  const handleRemoveForm = (index: number, type: 'employment' | 'coBorrower' | 'incomeBorrower' | 'incomeCoBorrower') => {
    if (type === 'employment') {
      setIsRemoveBorrowerForm(true);
      return setEmploymentForms((prevForms) => prevForms.filter((_, i) => i !== index));
    } else if (type === 'coBorrower') {
      setIsRemoveCoBorrowerForm(true);
      return setCoBorrowerForms((prevForms) => prevForms.filter((_, i) => i !== index));
    } else if (type === 'incomeBorrower') {
      setIsRemoveIncomeForm(true);
      return setIncomeBorrowerForms((prevForms) => prevForms.filter((_, i) => i !== index));
    } else {
      setIsRemoveIncomeCoBorrowerForm(true);
      return setIncomeCoBorrowerForms((prevForms) => prevForms.filter((_, i) => i !== index));
    }
  };

  const handleChangeRefactor = _.debounce(
    (value: string | number | boolean, name: string, index: number, type: 'employment' | 'coBorrower' | 'incomeBorrower' | 'incomeCoBorrower') => {
      setBackPage(false);
      setChangeIndex(index);

      if (name === 'lessThanTwoYears') {
        setHasLessThanTwoYearsConsent(value as boolean);
      } else if (name === 'lessThanTwoYearsCoBorrower') {
        setHasCoBorrowerLessThanTwoYearsConsent(value as boolean);
      }

      const item = templateConfig?.pages?.employedInformation?.employmentType?.options.find((option) => option.beValue === value);
      const isEndDateRequired = item ? item.endDateRequired : false;

      const handleEmploymentChange = (prev: any) =>
        prev.map((section: any, i: number) => {
          if (i !== index) {
            return section;
          }

          if (name === 'type') {
            if (value === 'Retired') {
              const result = {
                ...section,
                [name]: value,
                jobTitle: 'Retired',
                employment: 'Retired',
                currentEmployment: !isEndDateRequired || false,
                endDate: '',
              };
              return result;
            } else if (value === 'Unemployed') {
              const result = {
                ...section,
                [name]: value,
                jobTitle: 'Unemployed',
                employment: 'Unemployed',
                currentEmployment: !isEndDateRequired || false,
                endDate: '',
              };
              return result;
            } else {
              const wasRetiredOrUnemployed = section.type === 'Retired' || section.type === 'Unemployed';

              if (wasRetiredOrUnemployed && currentIndex.includes(index)) {
                setCurrentIndex(currentIndex.filter((i) => i !== index));
              }

              const result = {
                ...section,
                [name]: value,
                employment: wasRetiredOrUnemployed ? '' : section.employment,
                jobTitle: wasRetiredOrUnemployed ? '' : section.jobTitle,
                currentEmployment: wasRetiredOrUnemployed ? false : section.currentEmployment,
                endDate: wasRetiredOrUnemployed ? '' : section.endDate,
                monthly: section.monthly || '',
                startDate: section.startDate || '',
              };
              return result;
            }
          }

          if (name === 'currentEmployment') {
            const result = {
              ...section,
              [name]: value,
              endDate: value ? '' : section.endDate,
            };
            return result;
          }

          if (name === 'currentEmploymentCoBorrower') {
            const result = {
              ...section,
              [name]: value,
              endDateCoBorrower: value ? '' : section.endDateCoBorrower,
            };
            return result;
          }

          if (name === 'typeCoBorrower') {
            if (value === 'Retired') {
              const result = {
                ...section,
                [name]: value,
                jobTitleCoBorrower: 'Retired',
                employmentCoBorrower: 'Retired',
                currentEmploymentCoBorrower: !isEndDateRequired || false,
                endDateCoBorrower: '',
              };
              return result;
            } else if (value === 'Unemployed') {
              const result = {
                ...section,
                [name]: value,
                jobTitleCoBorrower: 'Unemployed',
                employmentCoBorrower: 'Unemployed',
                currentEmploymentCoBorrower: !isEndDateRequired || false,
                endDateCoBorrower: '',
              };
              return result;
            } else {
              const wasRetiredOrUnemployed = section.typeCoBorrower === 'Retired' || section.typeCoBorrower === 'Unemployed';

              if (wasRetiredOrUnemployed && currentCoBorrowerIndex.includes(index)) {
                setCurrentCoBorrowerIndex(currentCoBorrowerIndex.filter((i) => i !== index));
              }

              const result = {
                ...section,
                [name]: value,
                employmentCoBorrower: wasRetiredOrUnemployed ? '' : section.employmentCoBorrower,
                jobTitleCoBorrower: wasRetiredOrUnemployed ? '' : section.jobTitleCoBorrower,
                currentEmploymentCoBorrower: wasRetiredOrUnemployed ? false : section.currentEmploymentCoBorrower,
                endDateCoBorrower: wasRetiredOrUnemployed ? '' : section.endDateCoBorrower,
                monthlyCoBorrower: section.monthlyCoBorrower || '',
                startDateCoBorrower: section.startDateCoBorrower || '',
              };
              return result;
            }
          }

          return { ...section, [name]: value };
        });

      if (type === 'employment') {
        setEmploymentForms((prev: any) => {
          const newForms = handleEmploymentChange(prev);
          return newForms;
        });
        let obj: any = {};
        value = value === 'Current' ? !isCurrentEmployment : value;
        obj[name] = value;
        return;
      } else if (type === 'coBorrower') {
        setCoBorrowerForms((prev: any) => {
          const newForms = handleEmploymentChange(prev);
          return newForms;
        });
        let obj: any = {};
        value = value === 'Current' ? !isCurrentCoBorrowerEmployment : value;
        obj[name] = value;
        return;
      } else if (type === 'incomeBorrower') {
        return setIncomeBorrowerForms((prev: any) => prev.map((section: any, i: number) => (i === index ? { ...section, [name]: value } : section)));
      } else {
        return setIncomeCoBorrowerForms((prev: any) => prev.map((section: any, i: number) => (i === index ? { ...section, [name]: value } : section)));
      }
    },
    300,
  );

  const validateForms = useCallback(
    (forms: Record<string, any>[]) => {
      // check validation errors
      const hasDateErrors = Object.keys(dateValidationErrors).length > 0;
      if (hasDateErrors) {
        return false;
      }

      if (forms === incomeBorrowersForms || forms === incomeCoBorrowersForms) {
        return (
          forms.length === 0 ||
          forms.every((form) => {
            const monthlyValue = form.monthlyIncome?.replace(/[^0-9.]/g, '') || form.grossMonthlyIncomeCoBorrower?.replace(/[^0-9.]/g, '');
            const typeValue = form.typeIncome || form.typeIncomeCoBorrower;
            return typeValue?.trim() !== '' && monthlyValue && parseFloat(monthlyValue) > 0;
          })
        );
      }
      return forms.every((form) => {
        if (form.type === 'Retired') {
          const monthlyValue = form.monthly?.replace(/[^0-9.]/g, '');
          const dateRegex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/\d{4}$/;
          const retiredValidation =
            form.monthly &&
            form.monthly.trim() !== '' &&
            form.monthly !== '$' &&
            !isNaN(parseFloat(monthlyValue)) &&
            parseFloat(monthlyValue) > 0 &&
            form.startDate &&
            form.startDate.trim() !== '' &&
            dateRegex.test(form.startDate) &&
            form.employment === 'Retired' &&
            form.jobTitle === 'Retired';
          return retiredValidation;
        }

        if (form.type === 'Unemployed') {
          const monthlyValue = form.monthly?.replace(/[^0-9.]/g, '');
          const dateRegex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/\d{4}$/;
          const unemployedValidation =
            form.monthly &&
            form.monthly.trim() !== '' &&
            form.monthly !== '$' &&
            !isNaN(parseFloat(monthlyValue)) &&
            parseFloat(monthlyValue) > 0 &&
            form.startDate &&
            form.startDate.trim() !== '' &&
            dateRegex.test(form.startDate) &&
            form.employment === 'Unemployed' &&
            form.jobTitle === 'Unemployed';
          return unemployedValidation;
        }

        if (form.type && form.type !== 'Retired' && form.type !== 'Unemployed') {
          const monthlyValue = form.monthly?.replace(/[^0-9.]/g, '');
          const dateRegex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/\d{4}$/;

          const baseValidation =
            form.employment &&
            form.employment.trim() !== '' &&
            form.jobTitle &&
            form.jobTitle.trim() !== '' &&
            form.monthly &&
            form.monthly.trim() !== '' &&
            form.monthly !== '$' &&
            !isNaN(parseFloat(monthlyValue)) &&
            parseFloat(monthlyValue) > 0 &&
            form.startDate &&
            form.startDate.trim() !== '' &&
            dateRegex.test(form.startDate);

          const endDateValidation = form.currentEmployment ? true : form.endDate && form.endDate.trim() !== '' && dateRegex.test(form.endDate);

          const fullValidation = baseValidation && endDateValidation;
          return fullValidation;
        }

        return false;
      });
    },
    [
      employmentForms,
      coBorrowerForms,
      coBorrower,
      incomeBorrowersForms,
      indexIncomeForm,
      incomeCoBorrowersForms,
      indexIncomeCoBorrowerForm,
      hasLessThanTwoYears,
      dateValidationErrors,
    ],
  );

  const validateCoBorrowerForms = useCallback(
    (forms: CoBorrowerForm[]) => {
      // check co-borrower validation errors
      const hasCoBorrowerDateErrors = Object.keys(dateValidationErrors).some((key) => key.includes('startDateCoBorrower') || key.includes('endDateCoBorrower'));
      if (hasCoBorrowerDateErrors) {
        return false;
      }

      return forms.every((form, index) => {
        const monthlyValue = form.monthlyCoBorrower?.replace(/[^0-9.]/g, '');
        const dateRegex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/\d{4}$/;

        if (form.typeCoBorrower === 'Retired') {
          const retiredValidation =
            form.monthlyCoBorrower &&
            form.monthlyCoBorrower.trim() !== '' &&
            form.monthlyCoBorrower !== '$' &&
            !isNaN(parseFloat(monthlyValue)) &&
            parseFloat(monthlyValue) > 0 &&
            form.startDateCoBorrower &&
            form.startDateCoBorrower.trim() !== '' &&
            dateRegex.test(form.startDateCoBorrower) &&
            form.employmentCoBorrower === 'Retired' &&
            form.jobTitleCoBorrower === 'Retired';

          if (!retiredValidation) {
            console.warn(`Retired form at index ${index} is invalid`, form);
          }

          return retiredValidation;
        }

        if (form.typeCoBorrower === 'Unemployed') {
          const unemployedValidation =
            form.monthlyCoBorrower &&
            form.monthlyCoBorrower.trim() !== '' &&
            form.monthlyCoBorrower !== '$' &&
            !isNaN(parseFloat(monthlyValue)) &&
            parseFloat(monthlyValue) > 0 &&
            form.startDateCoBorrower &&
            form.startDateCoBorrower.trim() !== '' &&
            dateRegex.test(form.startDateCoBorrower) &&
            form.employmentCoBorrower === 'Unemployed' &&
            form.jobTitleCoBorrower === 'Unemployed';

          if (!unemployedValidation) {
            console.warn(`Unemployed form at index ${index} is invalid`, form);
          }

          return unemployedValidation;
        }

        if (form.typeCoBorrower && form.typeCoBorrower !== 'Retired' && form.typeCoBorrower !== 'Unemployed') {
          const baseValidation =
            form.employmentCoBorrower &&
            form.employmentCoBorrower.trim() !== '' &&
            form.jobTitleCoBorrower &&
            form.jobTitleCoBorrower.trim() !== '' &&
            form.monthlyCoBorrower &&
            form.monthlyCoBorrower.trim() !== '' &&
            form.monthlyCoBorrower !== '$' &&
            !isNaN(parseFloat(monthlyValue)) &&
            parseFloat(monthlyValue) > 0 &&
            form.startDateCoBorrower &&
            form.startDateCoBorrower.trim() !== '' &&
            dateRegex.test(form.startDateCoBorrower);

          const endDateValidation = form.currentEmploymentCoBorrower
            ? true
            : form.endDateCoBorrower && form.endDateCoBorrower.trim() !== '' && dateRegex.test(form.endDateCoBorrower);

          const fullValidation = baseValidation && endDateValidation;

          if (!fullValidation) {
            console.warn(`Employed form at index ${index} is invalid`, form);
          }

          return fullValidation;
        }

        console.warn(`Form at index ${index} is missing 'typeCoBorrower' or malformed`, form);
        return false;
      });
    },
    [dateValidationErrors],
  );

  useEffect(() => {
    if (isInitialLoad) {
      setIsInitialLoad(false);
      return;
    }
    const allFieldsFilled = coBorrower ? validateForms(employmentForms) && validateCoBorrowerForms(coBorrowerForms) : validateForms(employmentForms);

    let allFieldsIncomes = true;
    if (indexIncomeForm !== 0) {
      allFieldsIncomes = validateForms(incomeBorrowersForms);
    }
    if (indexIncomeCoBorrowerForm !== 0) {
      allFieldsIncomes = allFieldsIncomes && validateForms(incomeCoBorrowersForms);
    }
    const allFieldsValid = allFieldsFilled && allFieldsIncomes;

    if (formError !== allFieldsValid) {
      setFormError(allFieldsValid);
    }
  }, [
    employmentForms,
    coBorrowerForms,
    coBorrower,
    incomeBorrowersForms,
    indexIncomeForm,
    incomeCoBorrowersForms,
    indexIncomeCoBorrowerForm,
    hasLessThanTwoYears,
    isInitialLoad,
    formError,
  ]);

  let templateConfig = useAppSelector((state) => state.TemplateConfig);
  const coApplicantLabel = templateConfig?.pages?.personalInformation?.formInputs?.Coborrower?.coApplicantLabel || 'Co-Borrower';

  const coBorrowerText = templateConfig?.pages?.employedInformation?.coBorrower?.text;

  useEffect(() => {
    if (incomeBorrowersForms[indexIncomeForm - 1]?.monthlyIncome || monthly) {
      const currentEmployments = employmentForms.filter((emp) => emp.monthly && emp.currentEmployment);
      let income = 0;
      if (currentEmployments.length > 0) {
        income = currentEmployments.reduce((sum, emp) => {
          return sum + convertToNumber(emp.monthly || '0');
        }, 0);
      }
      const totalOtherIncome = incomeBorrowersForms.reduce((sum, form, index) => {
        if (form?.monthlyIncome && index !== indexIncomeForm) {
          return sum + convertToNumber(form.monthlyIncome);
        }
        return sum;
      }, 0);
      setOtherMonthlyIncome(totalOtherIncome + income);
    }
    if (incomeCoBorrowersForms[indexIncomeCoBorrowerForm - 1]?.grossMonthlyIncomeCoBorrower || monthlyCoBorrower) {
      const currentEmployments = coBorrowerForms.filter((emp) => emp.currentEmploymentCoBorrower);
      let incomeCoBorrower = 0;
      if (currentEmployments.length > 0) {
        incomeCoBorrower = currentEmployments.reduce((sum, emp) => {
          return sum + convertToNumber(emp.monthlyCoBorrower || '0');
        }, 0);
      }
      const totalOtherCoBorrowerIncome = incomeCoBorrowersForms.reduce((sum, form, index) => {
        if (form?.grossMonthlyIncomeCoBorrower && index !== indexIncomeCoBorrowerForm) {
          return sum + convertToNumber(form?.grossMonthlyIncomeCoBorrower);
        }
        return sum;
      }, 0);
      setOtherCoBorrowerMonthlyIncome(totalOtherCoBorrowerIncome + incomeCoBorrower);
    }
  }, [incomeBorrowersForms, monthly, incomeCoBorrowersForms, currentEmploymentCoBorrower, monthlyCoBorrower, currentEmployment]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!backPage && !isGoingBack) {
      const totalBorrower = true;
      if (totalBorrower) {
        if (!isRemoveBorrowerForm) {
          employmentForms.map((emp, indexEmploy) => {
            const calculatedMonths = emp?.startDate ? monthsBetweenDateAndToday(emp.startDate, 'startDate', emp.currentEmployment ? undefined : emp.endDate) : 0;
            const updatedBorrower = {
              ...auxLoanApplication.borrowers[0].employmentHistory[indexEmploy],
              type: emp?.type,
              employerName: emp.type === 'Retired' ? 'Retired' : emp?.employment,
              jobTitle: emp?.type === 'Retired' ? 'Retired' : emp?.jobTitle,
              startDate: emp?.startDate,
              endDate: emp?.endDate,
              grossMonthlyIncome: emp?.monthly ? convertToNumber(emp?.monthly) : 0,
              currentEmployment: emp?.currentEmployment ? true : false,
              totalMonths: calculatedMonths,
            };
            auxLoanApplication.borrowers[0].employmentHistory[indexEmploy] = updatedBorrower;
          });
        } else {
          setIsRemoveBorrowerForm(false);
          setIndexForm(indexForm - 1);
          auxLoanApplication.borrowers[0].employmentHistory.pop();
        }
      }
      if (coBorrower) {
        if (!isRemoveCoBorrowerForm) {
          coBorrowerForms.map((coEmp, indexCoEmploy) => {
            const calculatedMonths = coEmp?.startDateCoBorrower
              ? monthsBetweenDateAndToday(coEmp.startDateCoBorrower, 'startDateCoBorrower', coEmp.currentEmploymentCoBorrower ? undefined : coEmp.endDateCoBorrower)
              : 0;
            const updatedCoBorrower = {
              ...auxLoanApplication.borrowers[1].employmentHistory[indexCoEmploy],
              type: coEmp?.typeCoBorrower || '',
              employerName: coEmp?.typeCoBorrower === 'Retired' ? 'Retired' : coEmp?.employmentCoBorrower,
              jobTitle: coEmp?.typeCoBorrower === 'Retired' ? 'Retired' : coEmp?.jobTitleCoBorrower,
              startDate: coEmp.startDateCoBorrower,
              endDate: !coEmp.currentEmploymentCoBorrower && coEmp.endDateCoBorrower ? coEmp.endDateCoBorrower : '',
              grossMonthlyIncome: coEmp?.monthlyCoBorrower ? convertToNumber(coEmp?.monthlyCoBorrower) : 0,
              currentEmployment: coEmp?.currentEmploymentCoBorrower ? true : false,
              totalMonths: calculatedMonths,
            };
            auxLoanApplication.borrowers[1].employmentHistory[indexCoEmploy] = updatedCoBorrower;
          });
        } else {
          setIsRemoveCoBorrowerForm(false);
          setIndexCoBorrowerForm(indexCoBorrowerForm - 1);
          auxLoanApplication.borrowers[1].employmentHistory.pop();
        }
      }
      if (incomeBorrowersForms.length !== 0 || indexIncomeForm !== 0) {
        if (!isRemoveIncomeForm) {
          incomeBorrowersForms.map((income, indexIncome) => {
            const updateIncome = {
              ...auxLoanApplication.borrowers[0].otherIncome[indexIncome],
              incomeType: income.typeIncome,
              grossMonthlyIncome: convertToNumber(income.monthlyIncome),
            };
            auxLoanApplication.borrowers[0].otherIncome[indexIncome] = updateIncome;
          });
        } else {
          setIsRemoveIncomeForm(false);
          setIndexIncomeForm(indexIncomeForm - 1);
          auxLoanApplication.borrowers[0].otherIncome.pop();
        }
      }
      if (coBorrower) {
        if (incomeCoBorrowersForms.length !== 0 || indexIncomeCoBorrowerForm !== 0) {
          if (!isRemoveIncomeCoBorrowerForm) {
            incomeCoBorrowersForms.map((incomeCoBorrower, indexIncomeCoBorrower) => {
              const updateIncome = {
                ...auxLoanApplication.borrowers[1].otherIncome[indexIncomeCoBorrower],
                incomeType: incomeCoBorrower.typeIncomeCoBorrower,
                grossMonthlyIncome: convertToNumber(incomeCoBorrower.grossMonthlyIncomeCoBorrower),
              };
              auxLoanApplication.borrowers[1].otherIncome[indexIncomeCoBorrower] = updateIncome;
            });
          } else {
            setIsRemoveIncomeCoBorrowerForm(false);
            setIndexIncomeCoBorrowerForm(indexIncomeCoBorrowerForm - 1);
            auxLoanApplication.borrowers[1].otherIncome.pop();
          }
        }
      }

      dispatch({
        type: 'LoanApplication/updateValue',
        payload: { ...auxLoanApplication },
      });
    }
  }, [employmentForms, coBorrowerForms, incomeBorrowersForms, incomeCoBorrowersForms]); // eslint-disable-line react-hooks/exhaustive-deps

  const calculateTotalMonths = (employment: any[], isCurrent: string, startDate: string, endDate: string): number => {
    let totalMonths = 0;

    employment.forEach((emp, index) => {
      if (emp[startDate]) {
        const months = monthsBetweenDateAndToday(emp[startDate], startDate, emp[isCurrent] ? undefined : emp[endDate]);
        totalMonths += months;
        handleChangeRefactor(months, 'totalMonths', index, startDate.includes('CoBorrower') ? 'coBorrower' : 'employment');
      }
    });

    return totalMonths;
  };

  // Calculate total months from existing employment data on component mount/initialization
  useEffect(() => {
    if (employmentForms.length > 0 && totalBorrowerMonths === 0) {
      const totalFromExistingData = employmentForms.reduce((total, form) => {
        if (form.startDate) {
          const months = monthsBetweenDateAndToday(form.startDate, 'startDate', form.currentEmployment ? undefined : form.endDate);
          return total + months;
        }
        return total;
      }, 0);

      if (totalFromExistingData > 0) {
        setTotalBorrowerMonths(totalFromExistingData);
      }
    }
  }, [employmentForms, totalBorrowerMonths]); // Calculate when forms are initialized and totalMonths is still 0

  useEffect(() => {
    const borrowerTotal = calculateTotalMonths(employmentForms, 'currentEmployment', 'startDate', 'endDate');
    setTotalBorrowerMonths(borrowerTotal);
  }, [startDate, endDate, currentEmployment]); // eslint-disable-line react-hooks/exhaustive-deps

  // Calculate co-borrower total months from existing employment data on component mount/initialization
  useEffect(() => {
    if (coBorrowerForms.length > 0 && totalCoBorrowerMonths === 0) {
      const totalFromExistingData = coBorrowerForms.reduce((total, form) => {
        if (form.startDateCoBorrower) {
          const months = monthsBetweenDateAndToday(form.startDateCoBorrower, 'startDateCoBorrower', form.currentEmploymentCoBorrower ? undefined : form.endDateCoBorrower);
          return total + months;
        }
        return total;
      }, 0);

      if (totalFromExistingData > 0) {
        setTotalCoBorrowerMonths(totalFromExistingData);
      }
    }
  }, [coBorrowerForms, totalCoBorrowerMonths]); // Calculate when forms are initialized and totalMonths is still 0

  useEffect(() => {
    const coBorrowerTotal = calculateTotalMonths(coBorrowerForms, 'currentEmploymentCoBorrower', 'startDateCoBorrower', 'endDateCoBorrower');
    setTotalCoBorrowerMonths(coBorrowerTotal);
  }, [startDateCoBorrower, endDateCoBorrower, currentEmploymentCoBorrower]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleTotalYears = (totalMonths: number) => {
    const years = Math.floor(totalMonths / 12) || 0;
    const months = totalMonths % 12 || 0;
    return `Total employment history: ${years} years${months > 0 ? ` (${months} months)` : ''}`;
  };

  const formattedTotalBorrowerYears = useMemo(() => {
    return handleTotalYears(totalBorrowerMonths) || '';
  }, [totalBorrowerMonths]);
  const formattedTotalCoBorrowerYears = useMemo(() => {
    return handleTotalYears(totalCoBorrowerMonths) || '';
  }, [totalCoBorrowerMonths]);

  const shouldShowValidation = () => {
    return formSubmitted;
  };

  const getDisplayLabel = (backendValue: string) => {
    const option = templateConfig?.pages?.employedInformation?.employmentType?.options?.find((opt) => opt.beValue === backendValue);
    return option?.label || backendValue;
  };

  const handleWarningEmploymentChangesModalClose = () => {
    dispatch(setOpenWarningChangesEmploymentModal(false));
  };

  return (
    <>
      <form onSubmit={(e) => e.preventDefault()}>
        <EmploymentWrapper $isMobile={isMobile}>
          <Wrapper $gap={'1.563rem'}>
            <TextBlock
              text={`Consumer: ${loanApplication?.borrowers[0]?.borrowerFirstName} ${loanApplication?.borrowers[0]?.borrowerLastName}`}
              direction={'flex-start'}
              color="default"
              type={TypeOfText.H2}
              isHidden={templateConfig?.pages?.employedInformation?.currencyEmployment?.isHidden || false}
            />
            {employmentForms?.map((employment, index) => (
              <FormGroupComponent
                paddingButton={'0 !important'}
                key={index}
                data-testid="address-form"
                gap="1.563rem"
                onChange={(value, name) => {
                  handleChangeRefactor(value, name, index, 'employment');
                }}
              >
                <DropDownInput
                  dataTestId="employmentType"
                  label={templateConfig?.pages?.employedInformation?.employmentType?.label || 'Employment Type'}
                  placeHolder={templateConfig?.pages?.employedInformation?.employmentType?.placeHolder || 'Select one'}
                  options={templateConfig?.pages?.employedInformation?.employmentType?.options}
                  size="13%"
                  typeOfIcon="employment"
                  labelContainer={true}
                  isInfoModal={false}
                  hasAsterisk={true}
                  index={index}
                  propertyType={PropertyType.Type}
                  value={getDisplayLabel(employment.type) ?? ''}
                  isHidden={templateConfig?.pages?.employedInformation?.employmentType?.isHidden || false}
                  validationMessage={
                    shouldShowValidation() ? templateConfig?.pages?.employedInformation?.employmentType?.validationMessage || 'You must provide an employment type' : ''
                  }
                />
                <TextInput
                  dataTestId="employment"
                  label={templateConfig?.pages?.employedInformation?.yourEmployment?.label || 'Who is your employer?'}
                  hasAsterisk={employment.type !== 'Retired' && employment.type !== 'Unemployed'}
                  placeholder={
                    employment.type === 'Retired'
                      ? 'Retired'
                      : employment.type === 'Unemployed'
                        ? 'Unemployed'
                        : templateConfig?.pages?.employedInformation?.yourEmployment?.placeHolder || 'Company Name'
                  }
                  size="28%"
                  isDisabled={employment.type === 'Retired' || employment.type === 'Unemployed'}
                  typeOfIcon="employment"
                  isInfoModal={false}
                  index={index}
                  value={employment.employment || ''}
                  propertyType={PropertyType.EmployerName}
                  isHidden={templateConfig?.pages?.employedInformation?.yourEmployment?.isHidden || false}
                  validationMessage={
                    shouldShowValidation()
                      ? employment.type === 'Retired' || employment.type === 'Unemployed'
                        ? ''
                        : templateConfig?.pages?.employedInformation?.yourEmployment?.validationMessage || 'You must provide an employer name'
                      : ''
                  }
                />
                <TextInput
                  dataTestId="jobTitle"
                  label={templateConfig?.pages?.employedInformation?.jobTitle?.label || 'Job Title or Position'}
                  placeholder={
                    employment.type === 'Retired'
                      ? 'Retired'
                      : employment.type === 'Unemployed'
                        ? 'Unemployed'
                        : templateConfig?.pages?.employedInformation?.jobTitle?.placeHolder || 'Job Title'
                  }
                  isDisabled={employment.type === 'Retired' || employment.type === 'Unemployed'}
                  size="22%"
                  typeOfIcon="employment"
                  value={employment.jobTitle || ''}
                  isInfoModal={false}
                  hasAsterisk={employment.type !== 'Retired' && employment.type !== 'Unemployed'}
                  index={index}
                  propertyType={PropertyType.JobTitle}
                  isHidden={templateConfig?.pages?.employedInformation?.jobTitle?.isHidden || false}
                  validationMessage={shouldShowValidation() ? (employment.type === 'Retired' || employment.type === 'Unemployed' ? '' : 'You must provide a job title') : ''}
                />
                <TextInput
                  dataTestId="totalMonths"
                  label={templateConfig?.pages?.employedInformation?.grossMonthlyIncome?.label || 'Gross Monthly Income'}
                  hasAsterisk={true}
                  placeholder={templateConfig?.pages?.employedInformation?.grossMonthlyIncome?.placeHolder || '$0'}
                  size="32%"
                  typeOfIcon="dollar"
                  isInfoModal={false}
                  index={index}
                  propertyType={PropertyType.GrossMonthly}
                  value={formatNumber(loanApplication?.borrowers?.[0]?.employmentHistory[index]?.grossMonthlyIncome) || 0}
                  isHidden={templateConfig?.pages?.employedInformation?.grossMonthlyIncome?.isHidden || false}
                  validationMessage={
                    shouldShowValidation() ? templateConfig?.pages?.employedInformation?.grossMonthlyIncome?.validationMessage || 'You must provide a monthly gross income' : ''
                  }
                />
                <DateEmploymentPicker
                  dataTestId="startDate"
                  label={templateConfig?.pages?.employedInformation?.employmentStartDate?.label || 'Employment Start Date'}
                  hasAsterisk={true}
                  labelContainer={true}
                  placeholder={templateConfig?.pages?.employedInformation?.employmentStartDate?.placeHolder || 'MM/DD/YYYY'}
                  typeOfIcon="date"
                  propertyName={'startDate'}
                  size="24%"
                  index={index}
                  hasLessThanTwoYears={hasLessThanTwoYears}
                  propertyType={PropertyType.StartDate}
                  selectedOption={selectedOption}
                  value={loanApplication?.borrowers[0]?.employmentHistory[index]?.startDate || ''}
                  isHidden={templateConfig?.pages?.employedInformation?.employmentStartDate?.isHidden || false}
                  onValidationChange={(isValid) => handleDateValidationChange(isValid, index, 'startDate')}
                />
                <DateEmploymentPicker
                  dataTestId="endDate"
                  label={templateConfig?.pages?.employedInformation?.employmentEndDate?.label || 'Employment End Date'}
                  labelContainer={true}
                  hasAsterisk={!currentIndex.includes(index) && employment.type !== 'Retired' && employment.type !== 'Unemployed'}
                  placeholder={templateConfig?.pages?.employedInformation?.employmentEndDate?.placeHolder || 'MM/DD/YYYY'}
                  typeOfIcon="date"
                  propertyName={'endDate'}
                  size="25%"
                  index={index}
                  propertyType={PropertyType.EndDate}
                  selectedOption={selectedOption}
                  value={employment.endDate || ''}
                  isHidden={templateConfig?.pages?.employedInformation?.employmentEndDate?.isHidden || false}
                  isDisabled={currentIndex.includes(index) || employment.type === 'Retired' || employment.type === 'Unemployed'}
                  startDateValue={loanApplication?.borrowers[0]?.employmentHistory[index]?.startDate || ''}
                  onValidationChange={(isValid) => handleDateValidationChange(isValid, index, 'endDate')}
                />
                {loanApplication?.borrowers[0]?.employmentHistory[index]?.type !== 'Retired' && loanApplication?.borrowers[0]?.employmentHistory[index]?.type !== 'Unemployed' ? (
                  <RadioButton
                    label={templateConfig?.pages?.employedInformation?.current?.label || 'Current Employment'}
                    propertyType={PropertyType.CurrentEmployment}
                    key={index}
                    value={templateConfig?.pages?.employedInformation?.current?.placeholder || 'Current'}
                    beValue={templateConfig?.pages?.employedInformation?.current?.beValue || ''}
                    width={'15%'}
                    selectedOption={currentIndex.includes(index) ? 'Current' : ''}
                    setSelectedOption={setSelectedOption}
                    isHidden={templateConfig?.pages?.employedInformation?.employmentEndDate?.isHidden || false}
                    index={index}
                    setCurrentIndex={setCurrentIndex}
                    currentIndex={currentIndex}
                    checked={employmentForms[index].currentEmployment ?? false}
                  />
                ) : (
                  <></>
                )}
                {employmentForms.length > 0 && index > 0 && (
                  <Button
                    label={
                      //FIXME: enhance the span
                      <span>
                        <span>–</span>
                        <span className="text">Remove Employment</span>
                      </span>
                    }
                    onClick={() => handleRemoveForm(index, 'employment')}
                    data-testid="button"
                    variant="primary-link"
                    isHidden={templateConfig?.pages?.employedInformation?.button?.employment?.isHidden ?? false}
                    $alignRight="flex-end"
                  />
                )}
                {!isMobile && <Separator style={{ marginTop: '1.5rem' }} />}
                {index === 0 && (
                  <Button
                    label={templateConfig?.pages?.employedInformation?.button?.employment?.add || 'Employment'}
                    hasIcon={true}
                    onClick={() => handleAddForm('employment')}
                    data-testid="button"
                    variant="primary-link"
                    isHidden={templateConfig?.pages?.employedInformation?.button?.employment?.isHidden ?? false}
                    $alignRight="flex-end"
                  />
                )}
              </FormGroupComponent>
            ))}
            {!isMobile && employmentForms.length === 1 && <Separator />}
            <Button
              label={templateConfig?.pages?.employedInformation?.button?.income?.add || 'Additional Income'}
              hasIcon={true}
              onClick={() => handleAddForm('incomeBorrower')}
              data-testid="button"
              variant="primary-link"
              isHidden={templateConfig?.pages?.employedInformation?.button?.income?.isHidden ?? false}
              $alignRight="flex-end"
            />
            {indexIncomeForm !== 0 &&
              incomeBorrowersForms.map((_, index) => (
                <FormGroupComponent key={index} onChange={(value, name) => handleChangeRefactor(value, name, index, 'incomeBorrower')}>
                  <TextBlock
                    text={templateConfig?.pages?.employedInformation?.typeOfIncome?.titleIncome?.text || 'Other Sources of Income'}
                    direction={'flex-start'}
                    color="default"
                    type={TypeOfText.H2}
                    isHidden={templateConfig?.pages?.employedInformation?.typeOfIncome?.titleIncome?.isHidden || false}
                  />
                  <TextBlock
                    text={
                      templateConfig?.pages?.employedInformation?.typeOfIncome?.subTitleIncome?.text ||
                      'Alimony, child support or separate maintenance income need not be revealed if you do not wish to have it considered as a basis for repayment.'
                    }
                    direction={'flex-start'}
                    color="default"
                    type={TypeOfText.H3}
                    isHidden={templateConfig?.pages?.employedInformation?.typeOfIncome?.subTitleIncome?.isHidden || false}
                  />
                  <TextInput
                    dataTestId="typeIncome"
                    label={templateConfig?.pages?.employedInformation?.typeOfIncome?.label?.text || 'Type of Income'}
                    placeholder={templateConfig?.pages?.employedInformation?.typeOfIncome?.placeHolder?.text || 'Type of Income'}
                    size="25%"
                    typeOfIcon="employment"
                    isInfoModal={false}
                    hasAsterisk={templateConfig?.pages?.employedInformation?.typeOfIncome?.isRequired || false}
                    index={index}
                    propertyType={PropertyType.TypeIncome}
                    value={loanApplication?.borrowers[0]?.otherIncome?.[index]?.incomeType || ''}
                    isHidden={templateConfig?.pages?.employedInformation?.typeOfIncome?.placeHolder?.isHidden || false}
                  />
                  <TextInput
                    dataTestId="grossMonthlyIncome"
                    label={templateConfig?.pages?.employedInformation?.grossMonthlyIncome?.label || 'Gross Monthly Income'}
                    placeholder={templateConfig?.pages?.employedInformation?.grossMonthlyIncome?.placeHolder || '$0.00'}
                    size="25%"
                    typeOfIcon="dollar"
                    hasAsterisk={templateConfig?.pages?.employedInformation?.grossMonthlyIncome?.isRequired || false}
                    index={index}
                    isInfoModal={false}
                    value={formatNumber(loanApplication?.borrowers[0]?.otherIncome?.[index]?.grossMonthlyIncome) || ''}
                    propertyType={PropertyType.GrossMonthlyIncome}
                    isHidden={templateConfig?.pages?.employedInformation?.grossMonthlyIncome?.isHidden || false}
                    validationMessage={templateConfig?.pages?.employedInformation?.grossMonthlyIncome?.validationMessage || 'You must provide a monthly gross income'}
                  />
                  <Button
                    label={
                      //FIXME: enhance the span
                      <span>
                        <span>–</span>
                        <span className="text">Remove Income</span>
                      </span>
                    }
                    onClick={() => handleRemoveForm(index, 'incomeBorrower')}
                    data-testid="button"
                    variant="primary-link"
                    isHidden={templateConfig?.pages?.employedInformation?.typeOfIncome?.titleIncome?.isHidden || false}
                    $alignRight="flex-end"
                  />
                </FormGroupComponent>
              ))}
            {!hasLessThanTwoYears ? (
              <>
                <CheckConsent
                  text={templateConfig?.pages?.employedInformation?.consent?.text || 'I would like to continue my loan inquiry with less than 2 years employment history.'}
                  direction="center"
                  isHidden={templateConfig?.pages?.employedInformation?.consent?.isHidden || false}
                  type={'lessThanTwoYears'}
                  onChange={(value, name) => handleChangeRefactor(value, name, 0, 'employment')}
                />
                <TextBlock
                  text={templateConfig?.pages?.employedInformation?.information?.text || 'I would like to continue my loan inquiry with less than 2 years employment history.'}
                  direction={'flex-start'}
                  width="100%"
                  color="textPrimary"
                  isHidden={templateConfig?.pages?.employedInformation?.information?.isHidden && templateConfig?.pages?.employedInformation?.information?.text !== ''}
                  isCustomColor={true}
                  customColor="#ce3939"
                  type={TypeOfText.Subtitle}
                />
              </>
            ) : null}
          </Wrapper>
          <CurrentMonthlyWrapper>
            <TextBlock
              text={`${templateConfig?.pages?.employedInformation?.totalCurrencyIncome} ${formatNumber(otherMonthlyIncome) || 0}`}
              direction={'flex-start'}
              width="50%"
              type={TypeOfText.Subtitle}
              isCustomColor={true}
              customColor="#ffffff"
            />
            <TextBlock text={formattedTotalBorrowerYears || ''} direction={'flex-end'} width="50%" type={TypeOfText.Subtitle} isCustomColor={true} customColor="#ffffff" />
          </CurrentMonthlyWrapper>
          {coBorrower && (
            <>
              <Wrapper $gap={'1.563rem'}>
                <TextBlock
                  text={coBorrowerText?.replace('%%coApplicantLabel%%', coApplicantLabel) || 'Co-Borrower'}
                  direction={'flex-start'}
                  color="default"
                  type={TypeOfText.H2}
                  isHidden={templateConfig?.pages?.employedInformation?.currencyEmployment?.isHidden || false}
                />
                {coBorrowerForms.map((_, index) => (
                  <FormGroupComponent key={index} data-testid="address-form" gap="1.563rem" onChange={(value, name) => handleChangeRefactor(value, name, index, 'coBorrower')}>
                    <DropDownInput
                      dataTestId="employmentTypeCoBorrower"
                      label={templateConfig?.pages?.employedInformation?.employmentType?.label || 'Employment Type'}
                      placeHolder={templateConfig?.pages?.employedInformation?.employmentType?.placeHolder || 'Select one'}
                      options={templateConfig?.pages?.employedInformation?.employmentType?.options}
                      size="13%"
                      typeOfIcon="employment"
                      labelContainer={true}
                      isInfoModal={false}
                      index={index}
                      value={getDisplayLabel(coBorrowerForms[index]?.typeCoBorrower) || ''}
                      hasAsterisk={true}
                      propertyType={PropertyType.TypeCoBorrower}
                      isHidden={templateConfig?.pages?.employedInformation?.employmentType?.isHidden || false}
                      validationMessage={
                        shouldShowValidation() ? templateConfig?.pages?.employedInformation?.employmentType?.validationMessage || 'You must provide an employment type' : ''
                      }
                    />
                    <TextInput
                      dataTestId="employmentCoBorrower"
                      label={templateConfig?.pages?.employedInformation?.yourEmployment?.label || 'Who is your employer?'}
                      hasAsterisk={coBorrowerForms[index]?.typeCoBorrower !== 'Retired' && coBorrowerForms[index]?.typeCoBorrower !== 'Unemployed'}
                      placeholder={
                        loanApplication?.borrowers[1]?.employmentHistory[index]?.type === 'Retired'
                          ? 'Retired'
                          : loanApplication?.borrowers[1]?.employmentHistory[index]?.type === 'Unemployed'
                            ? 'Unemployed'
                            : templateConfig?.pages?.employedInformation?.yourEmployment?.placeHolder || 'Company Name'
                      }
                      size="28%"
                      isDisabled={
                        loanApplication?.borrowers[1]?.employmentHistory[index]?.type === 'Retired' ||
                        loanApplication?.borrowers[1]?.employmentHistory[index]?.type === 'Unemployed'
                      }
                      value={loanApplication?.borrowers[1]?.employmentHistory[index]?.employerName || ''}
                      typeOfIcon="employment"
                      isInfoModal={false}
                      index={index}
                      propertyType={PropertyType.EmploymentCoBorrower}
                      isHidden={templateConfig?.pages?.employedInformation?.yourEmployment?.isHidden || false}
                      validationMessage={
                        shouldShowValidation()
                          ? coBorrowerForms[index]?.typeCoBorrower === 'Retired' || coBorrowerForms[index]?.typeCoBorrower === 'Unemployed'
                            ? ''
                            : templateConfig?.pages?.employedInformation?.yourEmployment?.validationMessage || 'You must provide an employer name'
                          : ''
                      }
                    />
                    <TextInput
                      dataTestId="jobTitleCoBorrower"
                      label={templateConfig?.pages?.employedInformation?.jobTitle?.label || 'Job Title or Position'}
                      placeholder={
                        loanApplication?.borrowers[1]?.employmentHistory[index]?.type === 'Retired'
                          ? 'Retired'
                          : loanApplication?.borrowers[1]?.employmentHistory[index]?.type === 'Unemployed'
                            ? 'Unemployed'
                            : templateConfig?.pages?.employedInformation?.jobTitle?.placeHolder || 'Job Title'
                      }
                      size="22%"
                      isDisabled={
                        loanApplication?.borrowers[1]?.employmentHistory[index]?.type === 'Retired' ||
                        loanApplication?.borrowers[1]?.employmentHistory[index]?.type === 'Unemployed'
                      }
                      typeOfIcon="employment"
                      value={loanApplication?.borrowers[1]?.employmentHistory[index]?.jobTitle || ''}
                      isInfoModal={false}
                      index={index}
                      hasAsterisk={coBorrowerForms[index]?.typeCoBorrower !== 'Retired' && coBorrowerForms[index]?.typeCoBorrower !== 'Unemployed'}
                      propertyType={PropertyType.JobTitleCoBorrower}
                      isHidden={templateConfig?.pages?.employedInformation?.jobTitle?.isHidden || false}
                      validationMessage={
                        shouldShowValidation()
                          ? coBorrowerForms[index]?.typeCoBorrower === 'Retired' || coBorrowerForms[index]?.typeCoBorrower === 'Unemployed'
                            ? ''
                            : 'You must provide a job title'
                          : ''
                      }
                    />
                    <TextInput
                      dataTestId="grossMonthlyIncomeCoBorrower"
                      label={templateConfig?.pages?.employedInformation?.grossMonthlyIncome?.label || 'Gross Monthly Income'}
                      hasAsterisk={true}
                      placeholder={templateConfig?.pages?.employedInformation?.grossMonthlyIncome?.placeHolder || '$0'}
                      size="32%"
                      typeOfIcon="dollar"
                      isInfoModal={false}
                      value={formatNumber(loanApplication?.borrowers[1]?.employmentHistory[index]?.grossMonthlyIncome) || 0}
                      index={index}
                      propertyType={PropertyType.MonthlyCoBorrower}
                      isHidden={templateConfig?.pages?.employedInformation?.grossMonthlyIncome?.isHidden || false}
                      validationMessage={
                        shouldShowValidation() ? templateConfig?.pages?.employedInformation?.grossMonthlyIncome?.validationMessage || 'You must provide a monthly gross income' : ''
                      }
                    />
                    <DateEmploymentPicker
                      dataTestId="startDateCoBorrower"
                      label={templateConfig?.pages?.employedInformation?.employmentStartDate?.label || 'Employment Start Date'}
                      hasAsterisk={true}
                      labelContainer={true}
                      placeholder={templateConfig?.pages?.employedInformation?.employmentStartDate?.placeHolder || 'MM/DD/YYYY'}
                      typeOfIcon="date"
                      propertyName={'startDateCoBorrower'}
                      size="24%"
                      index={index}
                      hasLessThanTwoYears={hasCoBorrowerLessThanTwoYears}
                      propertyType={PropertyType.StartDateCoBorrower}
                      selectedOption={selectedCoBorrowerOption}
                      value={loanApplication?.borrowers[1]?.employmentHistory[index]?.startDate || ''}
                      isHidden={templateConfig?.pages?.employedInformation?.employmentStartDate?.isHidden || false}
                      onValidationChange={(isValid) => handleDateValidationChange(isValid, index, 'startDateCoBorrower')}
                    />
                    <DateEmploymentPicker
                      dataTestId="endDateCoBorrower"
                      label={templateConfig?.pages?.employedInformation?.employmentEndDate?.label || 'Employment End Date'}
                      labelContainer={true}
                      hasAsterisk={currentCoBorrowerIndex.includes(index) || loanApplication?.borrowers[1]?.employmentHistory[index]?.currentEmployment ? false : true}
                      placeholder={templateConfig?.pages?.employedInformation?.employmentEndDate?.placeHolder || 'MM/DD/YYYY'}
                      typeOfIcon="date"
                      propertyName={'endDateCoBorrower'}
                      size="25%"
                      index={index}
                      isDisabled={currentCoBorrowerIndex.includes(index) || loanApplication?.borrowers[1]?.employmentHistory[index]?.currentEmployment || false}
                      value={loanApplication?.borrowers[1]?.employmentHistory[index]?.endDate || ''}
                      propertyType={PropertyType.EndDateCoBorrower}
                      selectedOption={selectedCoBorrowerOption}
                      isHidden={templateConfig?.pages?.employedInformation?.employmentEndDate?.isHidden || false}
                      onValidationChange={(isValid) => handleDateValidationChange(isValid, index, 'endDateCoBorrower')}
                      startDateValue={loanApplication?.borrowers[1]?.employmentHistory[index]?.startDate || ''}
                    />
                    {loanApplication?.borrowers[1]?.employmentHistory[index]?.type !== 'Retired' &&
                    loanApplication?.borrowers[1]?.employmentHistory[index]?.type !== 'Unemployed' ? (
                      <RadioButton
                        label={templateConfig?.pages?.employedInformation?.current?.label || 'Current Employment'}
                        propertyType={PropertyType.CurrentEmploymentCoBorrower}
                        key={index}
                        value={templateConfig?.pages?.employedInformation?.current?.placeholder || 'Current'}
                        beValue={templateConfig?.pages?.employedInformation?.current?.beValue || ''}
                        width={'15%'}
                        selectedOption={selectedCoBorrowerOption}
                        setSelectedOption={setSelectedCoBorrowerOption}
                        isHidden={templateConfig?.pages?.employedInformation?.current?.isHidden || false}
                        index={index}
                        setCurrentIndex={setCurrentCoBorrowerIndex}
                        currentIndex={currentCoBorrowerIndex}
                        checked={coBorrowerForms[index].currentEmploymentCoBorrower ?? false}
                      />
                    ) : (
                      <></>
                    )}
                    {coBorrowerForms.length > 0 && index > 0 && (
                      <Button
                        label={
                          //FIXME: enhance the span
                          <span>
                            <span>–</span>
                            <span className="text">Remove Employment</span>
                          </span>
                        }
                        onClick={() => handleRemoveForm(index, 'coBorrower')}
                        data-testid="button"
                        variant="primary-link"
                        isHidden={templateConfig?.pages?.employedInformation?.button?.employment?.isHidden || false}
                        $alignRight="flex-end"
                      />
                    )}
                    {!isMobile && <Separator style={{ marginTop: '1.5rem' }} />}
                    {index === 0 && (
                      <Button
                        label={templateConfig?.pages?.employedInformation?.button?.employment?.add || 'Employment'}
                        hasIcon={true}
                        onClick={() => handleAddForm('coBorrower')}
                        data-testid="button"
                        variant="primary-link"
                        isHidden={templateConfig?.pages?.employedInformation?.button?.employment?.isHidden || false}
                        $alignRight="flex-end"
                      />
                    )}
                  </FormGroupComponent>
                ))}
                {!isMobile && coBorrowerForms.length === 1 && <Separator style={{ marginTop: '1.5rem' }} />}
                <Button
                  label={templateConfig?.pages?.employedInformation?.button?.income?.add || 'Additional Income'}
                  hasIcon={true}
                  onClick={() => handleAddForm('incomeCoBorrower')}
                  data-testid="button"
                  variant="primary-link"
                  isHidden={templateConfig?.pages?.employedInformation?.button?.income?.isHidden || false}
                  $alignRight="flex-end"
                />
                {indexIncomeCoBorrowerForm !== 0 &&
                  incomeCoBorrowersForms.map((_, index) => (
                    <FormGroupComponent key={index} onChange={(value, name) => handleChangeRefactor(value, name, index, 'incomeCoBorrower')}>
                      <TextBlock
                        text={templateConfig?.pages?.employedInformation?.typeOfIncome?.titleIncome?.text || 'Other Sources of Income'}
                        direction={'flex-start'}
                        color="default"
                        type={TypeOfText.H2}
                        isHidden={templateConfig?.pages?.employedInformation?.typeOfIncome?.titleIncome?.isHidden || false}
                      />
                      <TextBlock
                        text={
                          templateConfig?.pages?.employedInformation?.typeOfIncome?.subTitleIncome?.text ||
                          'Alimony, child support or separate maintenance income need not be revealed if you do not wish to have it considered as a basis for repayment.'
                        }
                        direction={'flex-start'}
                        color="default"
                        type={TypeOfText.H3}
                        isHidden={templateConfig?.pages?.employedInformation?.typeOfIncome?.subTitleIncome?.isHidden || false}
                      />
                      <TextInput
                        dataTestId="typeIncomeCoBorrower"
                        label={templateConfig?.pages?.employedInformation?.typeOfIncome?.label?.text || 'Type of Income'}
                        placeholder={templateConfig?.pages?.employedInformation?.typeOfIncome?.placeHolder?.text || 'Type of Income'}
                        size="25%"
                        typeOfIcon="employment"
                        isInfoModal={false}
                        hasAsterisk={true}
                        value={loanApplication?.borrowers[1]?.otherIncome[index]?.incomeType || ''}
                        propertyType={PropertyType.TypeIncomeCoBorrower}
                        isHidden={templateConfig?.pages?.employedInformation?.typeOfIncome?.label?.isHidden || false}
                      />
                      <TextInput
                        dataTestId="grossMonthlyIncomeCoBorrower"
                        label={templateConfig?.pages?.employedInformation?.grossMonthlyIncome?.label || 'Gross Monthly Income'}
                        placeholder={templateConfig?.pages?.employedInformation?.grossMonthlyIncome?.placeHolder || '$0.00'}
                        size="25%"
                        typeOfIcon="dollar"
                        hasAsterisk={templateConfig?.pages?.employedInformation?.grossMonthlyIncome?.isRequired || false}
                        isInfoModal={false}
                        value={formatNumber(loanApplication?.borrowers[1]?.otherIncome[index]?.grossMonthlyIncome) || ''}
                        propertyType={PropertyType.GrossMonthlyIncomeCoBorrower}
                        isHidden={templateConfig?.pages?.employedInformation?.grossMonthlyIncome?.isHidden || false}
                        validationMessage={templateConfig?.pages?.employedInformation?.grossMonthlyIncome?.validationMessage || 'You must provide a monthly gross income'}
                      />
                      <Button
                        label={
                          //FIXME: enhance the span
                          <span>
                            <span>–</span>
                            <span className="text">Remove Income</span>
                          </span>
                        }
                        onClick={() => handleRemoveForm(index, 'incomeCoBorrower')}
                        data-testid="button"
                        variant="primary-link"
                        isHidden={templateConfig?.pages?.employedInformation?.button?.income?.isHidden || false}
                        $alignRight="flex-end"
                      />
                    </FormGroupComponent>
                  ))}
                {!hasCoBorrowerLessThanTwoYears ? (
                  <>
                    <CheckConsent
                      text={templateConfig?.pages?.employedInformation?.consent?.text || 'I would like to continue my loan inquiry with less than 2 years employment history.'}
                      direction="center"
                      onChange={(value, name) => handleChangeRefactor(value, name, 0, 'coBorrower')}
                      isHidden={templateConfig?.pages?.employedInformation?.consent?.isHidden || false}
                      type={'lessThanTwoYearsCoBorrower'}
                    />
                    <TextBlock
                      text={templateConfig?.pages?.employedInformation?.information?.text || 'I would like to continue my loan inquiry with less than 2 years employment history.'}
                      direction={'flex-start'}
                      width="100%"
                      color="textPrimary"
                      isHidden={templateConfig?.pages?.employedInformation?.information?.isHidden && templateConfig?.pages?.employedInformation?.information?.text !== ''}
                      isCustomColor={true}
                      customColor="#ce3939"
                      type={TypeOfText.Subtitle}
                    />
                  </>
                ) : null}
              </Wrapper>
              <CurrentMonthlyWrapper>
                <TextBlock
                  text={`${templateConfig?.pages?.employedInformation?.totalCurrencyIncome}  ${formatNumber(otherCoBorrowerMonthlyIncome) || 0}`}
                  direction={'flex-start'}
                  customColor="#ffffff"
                  isCustomColor={true}
                  width="50%"
                  type={TypeOfText.Subtitle}
                />
                <TextBlock text={formattedTotalCoBorrowerYears || ''} direction={'flex-end'} width="50%" type={TypeOfText.Subtitle} isCustomColor={true} customColor="#ffffff" />
              </CurrentMonthlyWrapper>
            </>
          )}
        </EmploymentWrapper>
      </form>

      {openWarningChangesEmploymentModal && (
        <DefaultModal
          openModal={openWarningChangesEmploymentModal}
          setOpenModal={handleWarningEmploymentChangesModalClose}
          isErrorModal={false}
          infoModal={{
            body:
              templateConfig?.pages?.employedInformation?.modals?.warningMessageEmploymentChanges?.text ||
              'The employment information has changed from the last time it was entered. This may affect your loan qualification.',
            button: templateConfig?.pages?.employedInformation?.modals?.warningMessageEmploymentChanges?.button || 'Ok',
          }}
        />
      )}

      <NavigationButton
        variant="primary"
        nextButton={templateConfig?.pages?.employedInformation?.NavigationButton?.nextButton || 'Next'}
        isBackButton={templateConfig?.pages?.employedInformation?.NavigationButton?.backButton?.isHidden || false}
        backButton={templateConfig?.pages?.employedInformation?.NavigationButton?.backButton?.text || 'Back'}
        disabled={!formError || (!hasLessThanTwoYears && !hasLessThanTwoYearsConsent) || (coBorrower && !hasCoBorrowerLessThanTwoYears && !hasCoBorrowerLessThanTwoYearsConsent)}
        type="next"
        onAccess={() => {
          setFormSubmitted(true);
          onAccess('next');
        }}
        navigation={navigate}
        pageOrigin={PageOrigin.PERSONAL_INFORMATION}
        currentPage={PageOrigin.EMPLOYMENT_INFORMATION}
        destinationPage={PageOrigin.GOVERNMENT_MONITORING_INFORMATION}
        hasDataChanged={hasDataChanged}
        resetChangeDetection={resetChangeDetection}
      />
    </>
  );
};

export default EmploymentInformation;
